import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import CategoryCard from '../courses/CategoryCard';
import { getCategories, getCourses } from '../../services/api';

const CategoriesContainer = styled.section`
  background: ${props => props.theme.colors.divider};
  padding: 4rem 0;

  /* Small laptops and 125% zoom */
  @media (min-width: 1024px) and (max-width: 1366px) {
    padding: 3rem 0;
  }

  /* 13-15 inch laptops */
  @media (min-width: 1366px) and (max-width: 1600px) {
    padding: 3.5rem 0;
  }

  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }

  /* Extra small screens */
  @media (max-width: 480px) {
    padding: 1.5rem 0.5rem;
  }
`;

const CategoriesContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  text-align: center;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const SectionTitle = styled.h2`
  color: ${props => props.theme.colors.heading};
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;

  /* Small laptops and 125% zoom */
  @media (min-width: 1024px) and (max-width: 1366px) {
    font-size: 2.3rem;
  }

  /* 13-15 inch laptops */
  @media (min-width: 1366px) and (max-width: 1600px) {
    font-size: 2.4rem;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  /* Extra small screens */
  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const SectionSubtitle = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1.1rem;
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
  margin-top: 2rem;

  /* Small laptops and 125% zoom */
  @media (min-width: 1024px) and (max-width: 1366px) {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
  }

  /* 13-15 inch laptops */
  @media (min-width: 1366px) and (max-width: 1600px) {
    grid-template-columns: repeat(auto-fit, minmax(310px, 1fr));
    gap: 1.75rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  /* Extra small screens */
  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${props => props.theme.colors.error};
  font-size: 1.1rem;
`;

const CategoriesSection = () => {
  const [categories, setCategories] = useState([]);
  const [courseCounts, setCourseCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch categories
        const categoriesData = await getCategories();
        setCategories(categoriesData);

        // Fetch all courses to get counts
        const coursesData = await getCourses({ limit: 1000 });
        
        const counts = {};
        
        categoriesData.forEach(category => {
          const categoryCourses = coursesData.courses ? coursesData.courses.filter(course => 
            course.category._id === category._id
          ) : [];
          counts[category._id] = categoryCourses.length;
        });
        
        setCourseCounts(counts);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load categories. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <CategoriesContainer>
        <CategoriesContent>
          <SectionTitle>Industries We Serve</SectionTitle>
          <SectionSubtitle>Loading categories...</SectionSubtitle>
        </CategoriesContent>
      </CategoriesContainer>
    );
  }

  if (error) {
    return (
      <CategoriesContainer>
        <CategoriesContent>
          <SectionTitle>Industries We Serve</SectionTitle>
          <SectionSubtitle>
            We offer qualifications across a wide range of industries, helping professionals 
            in various sectors get their skills recognized and advance their careers.
          </SectionSubtitle>
          <ErrorContainer>
            {error}
          </ErrorContainer>
        </CategoriesContent>
      </CategoriesContainer>
    );
  }

  // Fallback if no categories loaded
  if (!categories || categories.length === 0) {
    return (
      <CategoriesContainer>
        <CategoriesContent>
          <SectionTitle>Industries We Serve</SectionTitle>
          <SectionSubtitle>No categories available at the moment.</SectionSubtitle>
        </CategoriesContent>
      </CategoriesContainer>
    );
  }

  return (
    <CategoriesContainer>
      <CategoriesContent>
        <SectionTitle>Industries We Serve</SectionTitle>
        <SectionSubtitle>
          We offer qualifications across a wide range of industries, helping professionals 
          in various sectors get their skills recognized and advance their careers.
        </SectionSubtitle>
        
        <CategoriesGrid>
          {categories.map((category) => (
            <CategoryCard
              key={category._id}
              category={category}
              courseCount={courseCounts[category._id] || 0}
            />
          ))}
        </CategoriesGrid>
      </CategoriesContent>
    </CategoriesContainer>
  );
};

export default CategoriesSection; 